/**
 * AWS Cognito Authentication Service Implementation
 * 
 * This is a concrete implementation of the AuthServiceInterface using AWS Cognito.
 * Can be easily replaced with another auth provider by implementing the same interface.
 */

import { Amplify } from 'aws-amplify';
import { 
  signUp as amplifySignUp, 
  confirmSignUp as amplifyConfirmSignUp, 
  signIn as amplifySignIn, 
  confirmSignIn as amplifyConfirmSignIn,
  signOut as amplifySignOut, 
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword 
} from 'aws-amplify/auth';
import { AuthServiceInterface } from './authService';
import ssoConfigs from '../endava-sso-config.json';
import { startEndavaSignIn, getEndavaSession, clearEndavaSession, endavaUser, takeEndavaSignInError } from './endava-sso';

const normalizeEmail = (email) => (email || '').trim().toLowerCase();

export class CognitoAuthService extends AuthServiceInterface {
  constructor() {
    super();
    this.initialized = false;
  }

  /**
   * Initialize AWS Cognito with configuration
   */
  async initialize(config) {
    if (this.initialized) {
      return;
    }

    this.endavaSso = config.aws.endavaSso || (config.aws.userPoolId === "us-east-1_n5Tp5dPdU" ? ssoConfigs.dev : undefined);
    Amplify.configure({
      Auth: {
        Cognito: {
          region: config.aws.region,
          userPoolId: config.aws.userPoolId,
          userPoolClientId: config.aws.userPoolWebClientId,
          identityPoolId: config.aws.identityPoolId,
          allowUnauthenticatedIdentities: false
        }
      }
    });

    this.initialized = true;
    console.log('Cognito Auth Service initialized');
  }

  /**
   * Sign up a new user with Cognito
   */
  async signInWithEndava() {
    try { await startEndavaSignIn(this.endavaSso); return { success: true }; }
    catch (error) { return { success: false, error: error.message }; }
  }

  async signUp(email, password) {
    try {
      const normalizedEmail = normalizeEmail(email);
      const result = await amplifySignUp({
        username: normalizedEmail,
        password: password,
        options: {
          userAttributes: {
            email: normalizedEmail
          }
        }
      });
      return { success: true, result };
    } catch (error) {
      console.error('Cognito signUp error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Confirm user sign up with verification code
   */
  async confirmSignUp(email, code) {
    try {
      await amplifyConfirmSignUp({
        username: normalizeEmail(email),
        confirmationCode: code
      });
      return { success: true };
    } catch (error) {
      console.error('Cognito confirmSignUp error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign in an existing user
   */
  async signIn(email, password) {
    clearEndavaSession(this.endavaSso);
    try {
      const normalizedEmail = normalizeEmail(email);
      console.log('Cognito: Attempting sign in for:', normalizedEmail);
      
      // Check if user is already authenticated
      try {
        const currentUser = await amplifyGetCurrentUser();
        if (currentUser) {
          console.log('User already authenticated:', currentUser.username);
          return { success: true, result: currentUser };
        }
      } catch (e) {
        // User not authenticated, proceed with sign in
      }
      
      const result = await amplifySignIn({
        username: normalizedEmail,
        password: password
      });
      console.log('Cognito sign in result:', result);
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { success: true, requiresNewPassword: true, result };
      }
      return { success: true, result };
    } catch (error) {
      console.error('Cognito signIn error:', error);
      
      // If user is already authenticated, treat as success
      if (error.message.includes('UserAlreadyAuthenticatedException')) {
        return { success: true, result: 'already_authenticated' };
      }
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Complete Cognito's temporary-password challenge for an invited user.
   */
  async completeNewPassword(newPassword) {
    try {
      const result = await amplifyConfirmSignIn({ challengeResponse: newPassword });
      if (!result.isSignedIn) {
        return {
          success: false,
          error: 'Cognito requires an additional sign-in step that this client does not support.'
        };
      }
      return { success: true, result };
    } catch (error) {
      console.error('Cognito complete new password error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    clearEndavaSession(this.endavaSso);
    try {
      await amplifySignOut();
      return { success: true };
    } catch (error) {
      console.error('Cognito signOut error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get the currently authenticated user
   */
  async getCurrentUser() {
    try {
      const tokens = await getEndavaSession(this.endavaSso);
      if (tokens) return endavaUser(tokens);
      const currentUser = await amplifyGetCurrentUser();
      const session = await fetchAuthSession();
      
      return {
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId || currentUser.username,
        accessToken: session.tokens?.accessToken?.toString(),
        identityId: session.identityId,
        hasAwsCredentials: !!session.credentials,
        signInUserSession: {
          getIdToken: () => ({
            getJwtToken: () => session.tokens?.idToken?.toString()
          })
        }
      };
    } catch (error) {
      console.log('No authenticated user:', error.message);
      return null;
    }
  }

  /**
   * Return a usable ID token for an API request. Amplify refreshes expired
   * Cognito tokens from the persisted refresh token when this is called.
   */
  async getIdToken() {
    const tokens = await getEndavaSession(this.endavaSso);
    if (tokens) return tokens.id_token;
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('Your login session has expired. Sign in again to continue.');
    }
    return idToken;
  }

  /**
   * Check current authentication state with detailed session info
   */
  async checkAuthState() {
    try {
      const tokens = await getEndavaSession(this.endavaSso);
      if (tokens) return { authenticated: true, user: endavaUser(tokens), session: tokens };
      console.log('Checking Cognito auth state...');
      const currentUser = await amplifyGetCurrentUser();
      
      // Get session and check Identity Pool credentials
      const session = await fetchAuthSession();
      
      console.log('=== COGNITO SESSION DETAILS ===');
      console.log('Access Token:', session.tokens?.accessToken ? 'Present' : 'Missing');
      console.log('ID Token:', session.tokens?.idToken ? 'Present' : 'Missing');
      console.log('Identity Pool ID:', session.identityId || 'Not available');
      console.log('AWS Credentials:', session.credentials ? 'Present' : 'Missing');
      
      if (session.credentials) {
        console.log('AWS Access Key ID:', session.credentials.accessKeyId?.substring(0, 8) + '...');
        console.log('AWS Session Token:', session.credentials.sessionToken ? 'Present' : 'Missing');
        console.log('Identity Pool Working: ✅');
      } else {
        console.log('Identity Pool Working: ❌');
      }
      console.log('===============================');
      
      const userData = {
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId || currentUser.username,
        accessToken: session.tokens?.accessToken?.toString(),
        identityId: session.identityId,
        hasAwsCredentials: !!session.credentials,
        signInUserSession: {
          getIdToken: () => ({
            getJwtToken: () => session.tokens?.idToken?.toString()
          })
        }
      };
      
      return {
        authenticated: true,
        user: userData,
        session: session
      };
    } catch (error) {
      console.log('Not authenticated:', error.message);
      return {
        authenticated: false,
        error: takeEndavaSignInError(this.endavaSso),
        user: null,
        session: null
      };
    }
  }

  /**
   * Initiate password reset flow
   */
  async resetPassword(email) {
    try {
      await amplifyResetPassword({ username: normalizeEmail(email) });
      return { success: true };
    } catch (error) {
      console.error('Cognito resetPassword error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Confirm password reset with verification code
   */
  async confirmResetPassword(email, code, newPassword) {
    try {
      await amplifyConfirmResetPassword({ 
        username: normalizeEmail(email), 
        confirmationCode: code, 
        newPassword: newPassword 
      });
      return { success: true };
    } catch (error) {
      console.error('Cognito confirmResetPassword error:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export a singleton instance
const cognitoAuthService = new CognitoAuthService();
export default cognitoAuthService;
